import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Logo, LogoIcon } from '../components/Logo';
import { BUSINESS_ADDRESS } from '../lib/businessAddress';
import { toast } from 'sonner';
import { captureLead } from '../lib/api';
import { InteractivePricingCalculator } from '../components/InteractivePricingCalculator';
import { BLOG_POSTS } from '../content/blogPosts';
import { 
  Phone, 
  Clock, 
  Calendar, 
  MessageSquare, 
  BarChart3,
  Layers,
  Shield,
  Users,
  Zap,
  ChevronRight, 
  Check,
  Star,
  ArrowRight,
  Headphones,
  Bot,
  FileText,
  TrendingUp,
  Globe,
  CheckCircle2,
  PhoneCall,
  Mic,
  Volume2,
  Building2,
  User,
  Mail,
  MapPin,
  Loader2,
  HeartPulse,
  Sparkles,
  Globe2,
} from 'lucide-react';

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'legal', label: 'Legal' },
  { value: 'retail', label: 'Retail' },
  { value: 'services', label: 'Services' },
  { value: 'plumber', label: 'Plumber' },
  { value: 'electrician', label: 'Electrician' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'cleaner', label: 'Cleaner' },
  { value: 'landscaper', label: 'Landscaper' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'carpenter', label: 'Carpenter' },
  { value: 'painter', label: 'Painter' },
  { value: 'roofer', label: 'Roofer' },
  { value: 'locksmith', label: 'Locksmith' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'auto_repair', label: 'Auto Repair' },
  { value: 'other', label: 'Other' },
];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showGetStarted, setShowGetStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [leadForm, setLeadForm] = useState({
    business_name: '',
    contact_name: '',
    phone: '',
    email: '',
    location: '',
    business_type: '',
    message: '',
  });
  const [calculatorSummary, setCalculatorSummary] = useState(null);
  const hasCalculatorSummaryValues = (() => {
    if (!calculatorSummary || typeof calculatorSummary !== 'object') return false;
    const usage = calculatorSummary.usage || {};
    const estimate = calculatorSummary.estimate || {};
    const calls = Number(usage.calls || 0);
    const sms = Number(usage.sms || 0);
    const fb = Number(usage.facebook_interactions || 0);
    const total = Number(estimate.total_monthly_aud || 0);
    return calls > 0 || sms > 0 || fb > 0 || total > 0;
  })();

  useEffect(() => {
    document.title = 'Desqly | AI Voice Agents for Service Businesses';

    const upsertMeta = (key, value, content) => {
      let element = document.head.querySelector(`meta[${key}="${value}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(key, value);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const upsertCanonical = (href) => {
      let element = document.head.querySelector('link[rel="canonical"]');
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', 'canonical');
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    const canonicalUrl = `${window.location.origin}/`;
    upsertMeta('name', 'description', 'Desqly helps service businesses answer calls and chat on SMS, WhatsApp, and Facebook with AI agents—plus call summaries, sentiment analysis, smart next steps, and analytics.');
    upsertMeta('name', 'keywords', 'Desqly, AI voice agent, omnichannel, SMS, WhatsApp, Facebook, AI receptionist, call answering, appointment booking, sentiment analysis, next best action, service business automation');
    upsertMeta('property', 'og:title', 'Desqly | AI Voice Agents for Service Businesses');
    upsertMeta('property', 'og:description', 'AI agents for calls, SMS, WhatsApp, and Facebook. Answer 24/7, qualify leads, and book appointments—omnichannel support for your business.');
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('name', 'twitter:title', 'Desqly | AI Voice Agents for Service Businesses');
    upsertMeta('name', 'twitter:description', 'Omnichannel AI: phone, SMS, WhatsApp, and Facebook. Lead capture, booking, sentiment, and analytics for service businesses.');
    upsertCanonical(canonicalUrl);
  }, []);

  useEffect(() => {
    if (searchParams.get('lead') === '1') {
      setShowGetStarted(true);
      const params = new URLSearchParams(searchParams);
      params.delete('lead');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleGetStarted = (summary = null) => {
    setCalculatorSummary(summary && typeof summary === 'object' ? summary : null);
    setShowGetStarted(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadForm.business_name.trim()) {
      toast.error('Business name is required');
      return;
    }
    if (!leadForm.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!leadForm.business_type) {
      toast.error('Please select your business type');
      return;
    }

    setSubmitting(true);
    try {
      await captureLead({
        ...leadForm,
        source: 'web',
        pricing_context: calculatorSummary,
      });
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch shortly.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setLeadForm({
      business_name: '',
      contact_name: '',
      phone: '',
      email: '',
      location: '',
      business_type: '',
      message: '',
    });
    setSubmitted(false);
    setShowGetStarted(false);
    setCalculatorSummary(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Logo size="default" />
            
            <nav className="hidden lg:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors">Features</a>
              <a href="#how-it-works" className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors">How It Works</a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors">Pricing</a>
              <Link to="/blog" className="text-sm font-medium text-slate-600 hover:text-violet-600 transition-colors">Blog</Link>
            </nav>

            <div className="flex items-center gap-3">
              {isAuthenticated ? (
                <Button asChild className="bg-teal-600 hover:bg-teal-700" data-testid="dashboard-btn">
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Button variant="ghost" asChild data-testid="login-btn">
                    <Link to="/login">Sign In</Link>
                  </Button>
                  <Button asChild className="bg-teal-600 hover:bg-teal-700 shadow-lg shadow-teal-200" data-testid="get-started-btn" onClick={handleGetStarted}>
                    <span className="cursor-pointer">Get Started</span>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-28 lg:pt-36 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-teal-50 via-white to-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <Badge className="mb-6 bg-teal-100 text-teal-700 hover:bg-teal-100 px-4 py-2 text-sm font-medium">
                <Zap className="w-4 h-4 mr-2" />
                AI-Powered Voice Agents for Your Business
              </Badge>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Never Miss Another
                <span className="block mt-2 bg-gradient-to-r from-teal-600 to-cyan-500 bg-clip-text text-transparent pb-2">
                  Business Call Again
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Deploy intelligent AI agents on{' '}
                <span className="font-medium text-slate-800">phone calls, SMS, WhatsApp, and Facebook</span>
                —answer 24/7, book appointments, capture leads, and support customers on the channels they already use.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-10">
                <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-base bg-teal-600 hover:bg-teal-700 shadow-xl shadow-teal-200" data-testid="hero-cta" onClick={handleGetStarted}>
                  <span className="cursor-pointer flex items-center">
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </span>
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Secure card capture at signup</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Setup in 5 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative">
              <div className="relative bg-gradient-to-br from-teal-500 to-cyan-600 rounded-3xl p-8 shadow-2xl shadow-teal-300/50">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-yellow-400 rounded-full blur-3xl opacity-50"></div>
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-teal-400 rounded-full blur-3xl opacity-40"></div>
                
                {/* Phone UI Mockup */}
                <div className="relative bg-white rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                      <Bot className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">AI Voice Agent</p>
                      <p className="text-sm text-green-500 flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        Active & Ready
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-xl p-4">
                      <p className="text-sm text-slate-500 mb-1">Incoming Call</p>
                      <p className="font-medium text-slate-900">+61 2 5135 5000</p>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-teal-50 rounded-xl p-4">
                      <Volume2 className="w-5 h-5 text-teal-600" />
                      <div className="flex-1">
                        <div className="flex gap-1">
                          {[...Array(12)].map((_, i) => (
                            <div 
                              key={i} 
                              className="w-1 bg-teal-400 rounded-full animate-pulse"
                              style={{ 
                                height: `${Math.random() * 20 + 10}px`,
                                animationDelay: `${i * 0.1}s`
                              }}
                            ></div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="text-sm text-slate-600 italic bg-slate-50 rounded-xl p-4">
                      "Hi! Thank you for calling. I'm your AI assistant. How can I help you today?"
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">98%</p>
                    <p className="text-xs text-slate-500">Call Answer Rate</p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">24/7</p>
                    <p className="text-xs text-slate-500">Always Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & scale metrics — illustrative targets; tune with your live ops data */}
      <section className="py-12 bg-slate-900" aria-label="Platform scale and reliability">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {[
              {
                value: '500+',
                label: 'Concurrent calls',
                hint: 'Peak simultaneous sessions',
                icon: Layers,
              },
              {
                value: '98%',
                label: 'Quality score',
                hint: 'Resolution & accuracy KPIs',
                icon: Star,
              },
              {
                value: '<1s',
                label: 'Response time',
                hint: 'Typical agent reply latency',
                icon: Zap,
              },
              {
                value: '99.9%',
                label: 'SLA uptime',
                hint: 'Monthly availability target',
                icon: Shield,
              },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-7 h-7 text-teal-400 mx-auto mb-3" aria-hidden />
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 tabular-nums">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-slate-200">{stat.label}</div>
                <p className="text-xs text-slate-500 mt-1 leading-snug">{stat.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              Powerful Features
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Calls & Messages—One AI Agent
            </h2>
            <p className="text-lg text-slate-600">
              Omnichannel support: the same AI helps customers on the phone and on the channels they already use—
              <span className="font-medium text-slate-800"> SMS, WhatsApp, and Facebook</span>—plus sentiment analysis
              and smart next action steps so your team always knows what to do next.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: '24/7 Call Answering',
                description: 'Never miss a call again. Your AI agent answers every call instantly, day or night, weekends and holidays included.',
                color: 'violet'
              },
              {
                icon: Globe2,
                title: 'Omnichannel support',
                description: 'One place for voice and messaging: customers can reach you by phone, SMS text, WhatsApp, or Facebook—same AI, consistent answers.',
                color: 'sky'
              },
              {
                icon: Calendar,
                title: 'Smart Appointment Booking',
                description: 'Let your AI agent check availability and book appointments directly into your calendar with automatic confirmations.',
                color: 'indigo'
              },
              {
                icon: MessageSquare,
                title: 'Lead Capture & Qualification',
                description: 'Capture caller details, qualify leads with custom questions, and get instant notifications for hot prospects.',
                color: 'blue'
              },
              {
                icon: FileText,
                title: 'Call Transcripts & Summaries',
                description: 'Detailed transcripts, AI summaries, and sentiment signals for every call and message—so context and tone are never lost.',
                color: 'cyan'
              },
              {
                icon: HeartPulse,
                title: 'Sentiment & intent analysis',
                description: 'See how customers feel and what they want across phone calls, SMS, WhatsApp, and Facebook—so you can prioritize follow-ups and coach your team with confidence.',
                color: 'red'
              },
              {
                icon: Sparkles,
                title: 'Smart next action steps',
                description: 'Get suggested follow-ups after each conversation—callbacks, bookings, complaints, or escalations—so nothing slips through the cracks.',
                color: 'amber'
              },
              {
                icon: BarChart3,
                title: 'Real-Time Analytics',
                description: 'Track call volumes, peak hours, common questions, sentiment trends, and conversion rates with our analytics dashboard.',
                color: 'teal'
              },
              {
                icon: Headphones,
                title: 'Custom Voice & Personality',
                description: 'Customize your AI agent\'s voice, tone, and responses to match your brand and business requirements.',
                color: 'emerald'
              },
            ].map((feature, i) => (
              <Card 
                key={i} 
                className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 bg-white"
                data-testid={`feature-card-${i}`}
              >
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-2xl bg-${feature.color}-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <feature.icon className={`w-7 h-7 text-${feature.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              Simple Setup
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Get Started in 3 Easy Steps
            </h2>
            <p className="text-lg text-slate-600">
              Setting up your AI voice agent takes just minutes. No technical skills required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                step: '01',
                title: 'Create Your Account',
                description: 'Create your account and tell us about your business. We\'ll customize your AI agent based on your industry.',
                icon: Users
              },
              {
                step: '02',
                title: 'Configure Your Agent',
                description: 'Set your business hours, services, and FAQs. Our AI learns your business to answer questions accurately.',
                icon: Bot
              },
              {
                step: '03',
                title: 'Start Taking Calls',
                description: 'Connect your phone number and start receiving calls. Your AI agent handles everything automatically.',
                icon: PhoneCall
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="text-7xl font-bold text-teal-100 absolute -top-4 -left-2">{item.step}</div>
                <div className="relative bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                  <div className="w-14 h-14 rounded-2xl bg-teal-600 flex items-center justify-center mb-6">
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              Perfect For
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Built for Service Businesses
            </h2>
            <p className="text-lg text-slate-600">
              Our AI voice agents are trusted by businesses across various industries.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { name: 'Plumbers', emoji: '🔧' },
              { name: 'Electricians', emoji: '⚡' },
              { name: 'HVAC', emoji: '❄️' },
              { name: 'Cleaners', emoji: '✨' },
              { name: 'Landscapers', emoji: '🌿' },
              { name: 'Contractors', emoji: '🏗️' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-slate-100 text-center hover:shadow-lg hover:border-teal-200 transition-all">
                <div className="text-4xl mb-3">{item.emoji}</div>
                <p className="font-medium text-slate-900">{item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              Pricing · AUD
            </Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Estimate your usage, then get started
            </h2>
            <p className="text-lg text-slate-600">
              Use the same calculator as logged-in pricing: pick your trade, model calls in minutes, SMS, and Facebook
              volume, and see a monthly estimate. When you&apos;re ready, share your details and we&apos;ll help you
              onboard.
            </p>
          </div>

          <div className="mb-16" data-testid="landing-pricing-calculator">
            <InteractivePricingCalculator variant="marketing" onGetStarted={handleGetStarted} />
          </div>

          {/* Additional Packages */}
          <div className="mt-8 text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Need More Minutes?</h3>
            <p className="text-slate-600 mb-4 max-w-2xl mx-auto">
              Every business has unique call volumes and workflows. If you need more coverage, advanced routing, or a
              fully tailored setup, our team can design a custom solution around your exact requirements.
            </p>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
              Tell us your goals and expected call demand, and we will recommend the right setup, rollout plan, and
              support model to help you scale confidently.
            </p>
            <div className="flex justify-center">
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={handleGetStarted}>
                Talk to Our Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">Blog</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Insights for tradies and operators</h2>
              <p className="text-slate-600">
                Tactical playbooks on missed calls, AI voice, lead conversion, and reducing admin overload.
              </p>
            </div>
            <Button variant="outline" asChild className="border-teal-200 text-teal-700 hover:text-teal-800">
              <Link to="/blog">
                View all articles
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[...BLOG_POSTS]
              .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
              .slice(0, 3)
              .map((post) => (
                <Card key={post.slug} className="border border-slate-200 hover:shadow-lg transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-3">
                      <span>{post.category}</span>
                      <span>•</span>
                      <span>{post.readingTime}</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 leading-tight">{post.title}</h3>
                    <p className="text-sm text-slate-600 mb-5">{post.metaDescription}</p>
                    <Button variant="ghost" asChild className="px-0 text-teal-700 hover:text-teal-800">
                      <Link to={`/blog/${post.slug}`}>
                        Read article
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-teal-100 text-teal-700 hover:bg-teal-100">
              FAQs
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'How natural does the AI voice sound?',
                a: 'Our AI uses advanced voice synthesis technology that sounds natural and professional. Most callers cannot distinguish it from a human receptionist.'
              },
              {
                q: 'Can I customize what the AI says?',
                a: 'Absolutely! You can customize greetings, responses to common questions, and the overall personality of your AI agent to match your brand.'
              },
              {
                q: 'What happens if the AI can\'t answer a question?',
                a: 'The AI will gracefully transfer the call or take a message with all relevant details. You\'ll receive instant notifications for calls that need follow-up.'
              },
              {
                q: 'How quickly can I get started?',
                a: 'Most businesses are up and running within 5-10 minutes. Simply sign up, answer a few questions about your business, and connect your phone number.'
              },
              {
                q: 'Can I keep my existing phone number?',
                a: 'Yes! You can either forward calls from your existing number to your AI agent, or port your number directly to our platform.'
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-slate-100">
                <h3 className="text-lg font-bold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-slate-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Never Miss a Call Again?
          </h2>
          <p className="text-xl text-teal-100 mb-10 max-w-2xl mx-auto">
            Join hundreds of service businesses already using AI voice agents to capture more leads and grow their revenue.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" variant="secondary" className="h-14 px-10 text-base font-semibold" onClick={handleGetStarted}>
              <span className="flex items-center cursor-pointer">
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </span>
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-10 text-base bg-transparent border-2 border-white text-white hover:bg-white/10" asChild>
              <a href="#pricing">View Pricing</a>
            </Button>
          </div>
          <p className="mt-6 text-teal-200 text-sm">
            Fast setup • Secure card capture • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <Logo size="default" linkTo="/" />
              <p className="text-slate-400 text-sm leading-relaxed mt-4">
                AI-powered voice agents that help service businesses capture more leads and deliver exceptional customer experiences.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li>
                  <Link to="/?lead=1" className="hover:text-white transition-colors">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><Link to="/legal" className="hover:text-white transition-colors">Terms and Conditions</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-center md:text-left">
              <p className="text-sm text-slate-400">{BUSINESS_ADDRESS}</p>
              <p className="text-sm text-slate-400">© 2026 Desqly.AI. All rights reserved.</p>
            </div>
            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <Globe className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Get Started Modal */}
      <Dialog open={showGetStarted} onOpenChange={setShowGetStarted}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-teal-600" />
              </div>
              {submitted ? 'Thank You!' : 'Get Started with Desqly'}
            </DialogTitle>
            <DialogDescription>
              {submitted 
                ? 'Our team will reach out to you shortly to help you get started.'
                : 'Tell us about your business and we\'ll help you get set up with an AI voice agent.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {submitted ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">We've received your information!</h3>
              <p className="text-slate-600 mb-6">
                A member of our team will contact you within 24 hours to discuss how Desqly can help your business.
              </p>
              <Button onClick={resetForm} className="bg-teal-600 hover:bg-teal-700">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleLeadSubmit}>
              <div className="grid gap-4 py-4">
                {hasCalculatorSummaryValues && (
                  <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3">
                    <p className="text-sm font-semibold text-teal-900 mb-1">Calculator summary attached</p>
                    <p className="text-xs text-slate-700">
                      {calculatorSummary.industry?.label || 'Selected industry'} • Calls (minutes):{' '}
                      {calculatorSummary.usage?.calls ?? 0} •
                      SMS: {calculatorSummary.usage?.sms ?? 0} • Facebook: {calculatorSummary.usage?.facebook_interactions ?? 0}
                    </p>
                    <p className="text-xs text-slate-700 mt-1">
                      Estimated total: ${Number(calculatorSummary.estimate?.total_monthly_aud || 0).toFixed(2)} AUD / month
                    </p>
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="lead-business">Business Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="lead-business"
                      value={leadForm.business_name}
                      onChange={(e) => setLeadForm({ ...leadForm, business_name: e.target.value })}
                      placeholder="Your Business Name"
                      className="pl-10"
                      data-testid="lead-business-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="lead-contact">Contact Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="lead-contact"
                        value={leadForm.contact_name}
                        onChange={(e) => setLeadForm({ ...leadForm, contact_name: e.target.value })}
                        placeholder="Your Name"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="lead-phone">Phone *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="lead-phone"
                        value={leadForm.phone}
                        onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                        placeholder="+61 400 000 000"
                        className="pl-10"
                        data-testid="lead-phone-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="lead-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="lead-email"
                        type="email"
                        value={leadForm.email}
                        onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                        placeholder="you@business.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="lead-location">Location</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="lead-location"
                        value={leadForm.location}
                        onChange={(e) => setLeadForm({ ...leadForm, location: e.target.value })}
                        placeholder="City, State"
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="lead-type">Business Type *</Label>
                  <Select 
                    value={leadForm.business_type}
                    onValueChange={(value) => setLeadForm({ ...leadForm, business_type: value })}
                  >
                    <SelectTrigger data-testid="lead-type-select">
                      <SelectValue placeholder="Select your business type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="lead-message">Message (Optional)</Label>
                  <Textarea
                    id="lead-message"
                    value={leadForm.message}
                    onChange={(e) => setLeadForm({ ...leadForm, message: e.target.value })}
                    placeholder="Tell us about your needs..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowGetStarted(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="submit-lead-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Get Started'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
